"use client";

import { useState, useEffect } from "react";
import { clog } from "@/lib/client-log";

// ── Types ─────────────────────────────────────────────────────────────────────

interface MatchRecapData {
  matchNumber:  number;
  round:        number;
  isDeadlock:   boolean;
  isTieBreaker: boolean;
  isRematch:    boolean;
  winnerId:     string | null;
  winnerStreak: number;
  commentary:   string | null;
  resolvedAt:   string;
  nameA:        { id: string; name: string; votes: number };
  nameB:        { id: string; name: string; votes: number };
  voters:       { displayName: string; role: string; votedFor: string; votedForId: string }[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  < 2)   return "just now";
  if (mins  < 60)  return `${mins}m ago`;
  if (hours < 24)  return `${hours}h ago`;
  if (days  === 1) return "yesterday";
  return `${days}d ago`;
}

function roleLabel(role: string) {
  if (role === "ADMIN")  return { text: "Admin",  cls: "bg-purple-100 text-purple-700" };
  if (role === "PARENT") return { text: "Parent", cls: "bg-blue-100 text-blue-700" };
  return                        { text: "Family", cls: "bg-green-100 text-green-700" };
}

// ── Modal ─────────────────────────────────────────────────────────────────────

function MatchRecapModal({
  matchNumber,
  onClose,
}: {
  matchNumber: number;
  onClose: () => void;
}) {
  const [data, setData]       = useState<MatchRecapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    clog.debug("match-recap", `fetching match #${matchNumber}`);

    async function fetchRecap() {
      try {
        const res = await fetch(`/api/match?n=${matchNumber}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: MatchRecapData = await res.json();
        clog.debug("match-recap", `loaded match #${matchNumber}`, {
          isDeadlock: json.isDeadlock,
          voters:     json.voters.length,
          commentary: json.commentary?.slice(0, 60) ?? null,
        });
        if (!cancelled) setData(json);
      } catch (err) {
        clog.error("match-recap", `fetch failed for #${matchNumber}`, {
          error: err instanceof Error ? err.message : String(err),
        });
        if (!cancelled) setError("Couldn't load match data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchRecap();
    return () => { cancelled = true; };
  }, [matchNumber]);

  const d = data;

  const totalVotes = d ? d.nameA.votes + d.nameB.votes : 0;
  const aPct       = d && totalVotes > 0 ? Math.round((d.nameA.votes / totalVotes) * 100) : 50;
  const bPct       = 100 - aPct;

  const winnerName = d
    ? (d.winnerId === d.nameA.id ? d.nameA.name : d.nameB.name)
    : null;
  const loserName  = d
    ? (d.winnerId === d.nameA.id ? d.nameB.name : d.nameA.name)
    : null;

  // Group voters by who they voted for
  const aVoters = d?.voters.filter((v) => v.votedForId === d.nameA.id) ?? [];
  const bVoters = d?.voters.filter((v) => v.votedForId === d.nameB.id) ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6"
      style={{ background: "rgba(15,5,32,0.75)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── header ── */}
        <div className={`px-6 pt-6 pb-5 ${
          d?.isDeadlock
            ? "bg-gradient-to-br from-amber-500 to-orange-500"
            : "bg-gradient-to-br from-[#1e0b4a] to-[#3b0764]"
        }`}>
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              {d?.isDeadlock && (
                <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-black text-white">⚡ Deadlock</span>
              )}
              {d?.isTieBreaker && (
                <span className="rounded-full bg-yellow-300/30 px-2.5 py-0.5 text-[11px] font-black text-yellow-100">Tie-breaker</span>
              )}
              {d?.isRematch && (
                <span className="rounded-full bg-orange-300/30 px-2.5 py-0.5 text-[11px] font-black text-orange-100">Rematch</span>
              )}
              {d && !d.isDeadlock && d.winnerStreak >= 2 && (
                <span className="rounded-full bg-purple-300/30 px-2.5 py-0.5 text-[11px] font-black text-purple-100">W{d.winnerStreak} streak</span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-white/50 hover:text-white text-xl leading-none shrink-0 mt-0.5 select-none"
              style={{ WebkitUserSelect: "none", userSelect: "none" }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className={`text-xs mb-4 ${d?.isDeadlock ? "text-amber-100" : "text-purple-400"}`}>
            Match #{matchNumber}
            {d && ` · Round ${d.round}`}
            {d && ` · ${timeAgo(d.resolvedAt)}`}
          </div>

          {/* scoreboard */}
          {loading ? (
            <div className="flex gap-3">
              <div className="flex-1 rounded-xl bg-white/10 h-16 animate-pulse" />
              <div className="flex flex-col items-center justify-center px-2">
                <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold text-white/60">VS</span>
              </div>
              <div className="flex-1 rounded-xl bg-white/10 h-16 animate-pulse" />
            </div>
          ) : error ? (
            <p className="text-red-300 text-sm text-center py-4">{error}</p>
          ) : d ? (
            <>
              <div className="flex items-stretch gap-3">
                {/* Name A */}
                <div className={`flex-1 rounded-xl px-4 py-3 text-center relative ${
                  !d.isDeadlock && d.winnerId === d.nameA.id
                    ? "bg-white/20 ring-2 ring-white/40"
                    : "bg-white/10"
                }`}>
                  {!d.isDeadlock && d.winnerId === d.nameA.id && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs">🏆</div>
                  )}
                  <div className="font-black text-white text-base leading-tight mt-1">{d.nameA.name}</div>
                  <div className="text-3xl font-black text-white mt-1">{d.nameA.votes}</div>
                  <div className="text-[10px] text-white/50 mt-0.5">{aPct}%</div>
                </div>

                <div className="flex flex-col items-center justify-center gap-1 px-1">
                  <div className="w-px flex-1 bg-white/15" />
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                    d.isDeadlock ? "bg-amber-900/40 text-amber-100" : "bg-purple-600 text-white"
                  }`}>VS</span>
                  <div className="w-px flex-1 bg-white/15" />
                </div>

                {/* Name B */}
                <div className={`flex-1 rounded-xl px-4 py-3 text-center relative ${
                  !d.isDeadlock && d.winnerId === d.nameB.id
                    ? "bg-white/20 ring-2 ring-white/40"
                    : "bg-white/10"
                }`}>
                  {!d.isDeadlock && d.winnerId === d.nameB.id && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs">🏆</div>
                  )}
                  <div className="font-black text-white text-base leading-tight mt-1">{d.nameB.name}</div>
                  <div className="text-3xl font-black text-white mt-1">{d.nameB.votes}</div>
                  <div className="text-[10px] text-white/50 mt-0.5">{bPct}%</div>
                </div>
              </div>

              {/* split bar */}
              {totalVotes > 0 && (
                <div className="mt-3 flex h-1.5 rounded-full overflow-hidden gap-px">
                  <div
                    className={`h-full rounded-l-full transition-all ${d.isDeadlock ? "bg-amber-200" : "bg-white/60"}`}
                    style={{ width: `${aPct}%` }}
                  />
                  <div
                    className={`h-full rounded-r-full transition-all ${d.isDeadlock ? "bg-amber-800/40" : "bg-white/20"}`}
                    style={{ width: `${bPct}%` }}
                  />
                </div>
              )}

              {d.isDeadlock && (
                <p className="text-center text-amber-100 text-xs mt-2 font-semibold">
                  No winner — both names returned to the pool
                </p>
              )}
            </>
          ) : null}
        </div>

        {/* ── body ── */}
        <div className="px-6 py-5 space-y-5 max-h-[55vh] overflow-y-auto">

          {loading && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 bg-purple-50 rounded-lg animate-pulse" />
              ))}
            </div>
          )}

          {!loading && d && (
            <>
              {/* AI commentary */}
              {d.commentary && (
                <div className="rounded-xl bg-purple-50 border border-purple-100 px-4 py-3">
                  <div className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">
                    🎙 Commentary
                  </div>
                  <p className="text-purple-700 text-sm italic leading-relaxed">"{d.commentary}"</p>
                </div>
              )}

              {/* voter breakdown */}
              {d.voters.length > 0 ? (
                <div>
                  <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-2">
                    How the votes fell
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Column A */}
                    <div>
                      <div className="text-[11px] font-black text-purple-600 mb-1.5 truncate">{d.nameA.name}</div>
                      <div className="space-y-1">
                        {aVoters.length > 0 ? aVoters.map((v, i) => {
                          const rl = roleLabel(v.role);
                          return (
                            <div key={i} className="flex items-center gap-1.5 rounded-lg bg-purple-50 px-2 py-1.5">
                              <span className="text-xs flex-1 font-semibold text-purple-800 truncate">
                                {v.displayName || "—"}
                              </span>
                              <span className={`rounded px-1.5 text-[9px] font-black shrink-0 ${rl.cls}`}>
                                {rl.text}
                              </span>
                            </div>
                          );
                        }) : (
                          <p className="text-[11px] text-gray-300 italic">No votes</p>
                        )}
                      </div>
                    </div>

                    {/* Column B */}
                    <div>
                      <div className="text-[11px] font-black text-purple-600 mb-1.5 truncate">{d.nameB.name}</div>
                      <div className="space-y-1">
                        {bVoters.length > 0 ? bVoters.map((v, i) => {
                          const rl = roleLabel(v.role);
                          return (
                            <div key={i} className="flex items-center gap-1.5 rounded-lg bg-purple-50 px-2 py-1.5">
                              <span className="text-xs flex-1 font-semibold text-purple-800 truncate">
                                {v.displayName || "—"}
                              </span>
                              <span className={`rounded px-1.5 text-[9px] font-black shrink-0 ${rl.cls}`}>
                                {rl.text}
                              </span>
                            </div>
                          );
                        }) : (
                          <p className="text-[11px] text-gray-300 italic">No votes</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-sm text-center py-2">No voter data recorded for this match.</p>
              )}

              {/* result summary line */}
              {!d.isDeadlock && winnerName && loserName && (
                <div className="rounded-xl bg-green-50 border border-green-100 px-4 py-3 text-center">
                  <span className="font-black text-green-700">{winnerName}</span>
                  <span className="text-green-600 text-sm"> defeated </span>
                  <span className="font-black text-green-700">{loserName}</span>
                  {d.winnerStreak >= 2 && (
                    <div className="text-[11px] text-green-500 mt-0.5">
                      {winnerName} now on a {d.winnerStreak}-match win streak
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

export default MatchRecapModal;
